import { keyBy } from "lodash";
import useEvent from "react-use-event-hook";
import { useRecoilState, useSetRecoilState } from "recoil";

import {
  isEntityServerSidePaginated,
  mergeRowIntoGroupedTableResults,
  tableRowsAtomFamily,
  tableRowsGroupValuesAtomFamily,
  updateListEntity,
  type ITableDto,
  type RydeUiUnit,
} from "@ryde/shared";

import { useStarredRows } from "./dynamic-tables";
import { useHighlightEdited } from "./use-highlight-edited";
import { useShiftServerRowsCount } from "./use-shift-server-rows-count";

export const useTableListActions = (entity: RydeUiUnit) => {
  const [tableDtos, setTableDtos] = useRecoilState(tableRowsAtomFamily(entity));
  const setGroupedTableRows = useSetRecoilState(
    tableRowsGroupValuesAtomFamily(entity),
  );
  const isServerPaginated = isEntityServerSidePaginated(entity);
  const { setHighlightedRows } = useHighlightEdited(entity);
  const { removeStarredIds } = useStarredRows(entity);
  const { shiftServerRowsCount } = useShiftServerRowsCount(entity);

  const addTableDto = useEvent((dto: ITableDto): void => {
    setTableDtos([...(tableDtos || []), { ...dto }]);
    shiftServerRowsCount(1);

    if (dto?.id) {
      setHighlightedRows([dto.id]);
    }
  });

  const replaceTableDto = useEvent((dtoUpdated: ITableDto): void => {
    updateListEntity(setTableDtos, dtoUpdated);

    if (isServerPaginated) {
      setGroupedTableRows(groups =>
        mergeRowIntoGroupedTableResults({ groups, dto: dtoUpdated }),
      );
    }

    if (dtoUpdated?.id) {
      setHighlightedRows([dtoUpdated.id]);
    }
  });

  const bulkAddTableDto = useEvent((newDtos: ITableDto[]): void => {
    const updatedDtos = [...(tableDtos || []), ...newDtos];
    setTableDtos(updatedDtos);
    shiftServerRowsCount(newDtos.length);
  });

  const bulkReplaceTableDto = useEvent((updatedDtos: ITableDto[]): void => {
    const updatedDtoIds = keyBy(updatedDtos, "id");
    const updatedTableDtos = tableDtos?.map(dto => {
      if (updatedDtoIds?.[dto.id]) {
        return updatedDtoIds[dto.id];
      }

      return dto;
    });
    setTableDtos(updatedTableDtos);
  });

  // TODO get rid of code duplication `useDynamicFormsListActions`
  const bulkRemoveTableDto = useEvent((ids: number[]): void => {
    const idsSet = new Set(ids);
    const updatedDtos = tableDtos?.filter(dto => !idsSet.has(dto.id));
    setTableDtos(updatedDtos);
    shiftServerRowsCount(-ids.length);
    removeStarredIds(ids);
  });

  const removeTableDto = useEvent((id: number): void => {
    const updatedDtos = tableDtos?.filter(dto => dto.id !== id);
    setTableDtos(updatedDtos);
    shiftServerRowsCount(-1);
    removeStarredIds([id]);
  });

  return {
    addTableDto,
    replaceTableDto,
    bulkAddTableDto,
    bulkReplaceTableDto,
    bulkRemoveTableDto,
    removeTableDto,
  };
};
